import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import styles from "./WhatYouGet.module.scss";

const benefits = [
  {
    title: "Hours back to sell",
    description: "Less time wrangling tools, more time in front of buyers.",
  },
  {
    title: "Nothing slips",
    description: "Every follow-up, signal, and stakeholder is accounted for.",
  },
  {
    title: "Deals move faster",
    description: "Risks surface early and the next step is always ready.",
  },
  {
    title: "Full context, every action",
    description: "Make the right call with the whole story in front of you.",
  },
  {
    title: "A day already planned",
    description: "Start with clear priorities instead of a scramble.",
  },
  {
    title: "A calmer workspace",
    description: "No clutter, no tab-hopping, no guessing what's next.",
  },
  {
    title: "Better every week",
    description: "Coaching in the flow turns habits into closed deals.",
  },
  {
    title: "Zero tool hopping",
    description: "Every piece of information exactly where you need it.",
  },
];

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M3 7.5L5.5 10L11 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WhatYouGet = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      id="what-you-get"
      ref={ref}
      className={styles.section}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container-custom">
        <motion.div className={styles.badge} variants={itemVariants}>
          <span className={styles.badgeDot} aria-hidden="true" />
          What You Get
        </motion.div>

        <motion.h2 className={styles.headline} variants={itemVariants}>
          What changes when every rep has a Chief of Staff
        </motion.h2>

        <motion.p className={styles.lead} variants={itemVariants}>
          Vertik isn&apos;t more software to manage. It&apos;s time, clarity, and
          momentum handed back to the people who sell.
        </motion.p>

        <motion.div className={styles.grid} variants={itemVariants}>
          {benefits.map((benefit) => (
            <motion.article
              key={benefit.title}
              className={styles.benefitItem}
              data-ui="benefit-item"
              variants={itemVariants}
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <span className={styles.checkIcon}>
                <CheckIcon />
              </span>
              <div>
                <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                <p className={styles.benefitDescription}>{benefit.description}</p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default WhatYouGet;
